import { format } from 'date-fns';
import { Row } from '@client/new/components/StandardTable/StandardTable';

export type ScheduledWorkout = {
  id: number;
  publishedAt: string;
};

export type ScheduledItem = ScheduledWorkout & {
  title: string;
  row: Row<number>;
};

export type ScheduleGroup<T extends ScheduledWorkout = ScheduledItem> = {
  key: number;
  publishedAt: string;
  items: Array<T>;
};

export type ScheduleGroupRequest = {
  publishedAt: string;
  workoutIds: Array<number>;
};

const MINUTE_MS = 60_000;

export const formatGroupTime = (publishedAt: string) =>
  format(new Date(publishedAt), 'EEE, MMM d yyyy, h:mma');

const toMinuteKey = (publishedAt: string) =>
  Math.floor(Date.parse(publishedAt) / MINUTE_MS);

const toMinuteIso = (key: number) => new Date(key * MINUTE_MS).toISOString();

export const groupByPublishMinute = <T extends ScheduledWorkout>(
  items: Array<T>,
): Array<ScheduleGroup<T>> => {
  const byKey = new Map<number, Array<T>>();
  items.forEach((item) => {
    const key = toMinuteKey(item.publishedAt);
    byKey.set(key, [...(byKey.get(key) ?? []), item]);
  });

  return [...byKey.entries()]
    .sort(([a], [b]) => a - b)
    .map(([key, members]) => ({
      key,
      publishedAt: toMinuteIso(key),
      items: members,
    }));
};

// The backend rejects a request that leaves out a workout already scheduled
// at the target minute, so every request carries the whole resulting group.
export const toGroupRequest = (
  groups: Array<ScheduleGroup<ScheduledWorkout>>,
  publishedAt: string,
  workoutIds: Array<number>,
  index?: number,
): ScheduleGroupRequest => {
  const key = toMinuteKey(publishedAt);
  const others = (groups.find((group) => group.key === key)?.items ?? [])
    .map((item) => item.id)
    .filter((id) => !workoutIds.includes(id));
  const at = index ?? others.length;

  return {
    publishedAt: toMinuteIso(key),
    workoutIds: [...others.slice(0, at), ...workoutIds, ...others.slice(at)],
  };
};

export const applyGroupRequest = <T extends ScheduledWorkout>(
  items: Array<T>,
  request: ScheduleGroupRequest,
): Array<T> => {
  const listed = request.workoutIds.flatMap((id) => {
    const item = items.find((candidate) => candidate.id === id);
    return item ? [{ ...item, publishedAt: request.publishedAt }] : [];
  });
  const rest = items.filter((item) => !request.workoutIds.includes(item.id));
  return [...rest, ...listed];
};

export type ExistingScheduleTime = {
  key: number;
  publishedAt: string;
  count: number;
};

export const toExistingScheduleTimes = (
  scheduled: Array<ScheduledWorkout>,
  excludedIds: Array<number>,
  now: Date,
): Array<ExistingScheduleTime> =>
  groupByPublishMinute(scheduled.filter((w) => !excludedIds.includes(w.id)))
    .filter((group) => Date.parse(group.publishedAt) > now.getTime())
    .map((group) => ({
      key: group.key,
      publishedAt: group.publishedAt,
      count: group.items.length,
    }));

export const findScheduleTime = (
  times: Array<ExistingScheduleTime>,
  publishedAt: string | undefined,
) =>
  publishedAt
    ? times.find((time) => time.key === toMinuteKey(publishedAt))
    : undefined;
